import { GraphQLNonNull } from 'graphql'
import { Context } from '../shared'
import { ContrastQueryParameters } from '../endpoint-helpers'
import {
  libraryUsagePagingArgs,
  LibraryUsagePagingArgs
} from '../types/library-usage-paging-args'
import {
  libraryUsagePageType,
  LibraryUsagePage
} from '../types/library-usage-page'
import { ContrastGraphQLFieldConfig } from '..'
import { LibraryUsageFilterArg } from '../types/library-usage-filter-arg'
import { SkeletorTableState } from '@contrast-security-inc/design-system-tables'

function appendSortToParams(
  params: ContrastQueryParameters,
  orderBy: SkeletorTableState['sorting'] | undefined
) {
  if (orderBy) {
    params.append('sortBy', `${orderBy[0].id}`)
    params.append('sortDirection', `${orderBy[0].desc ? 'DESC' : 'ASC'}`)
  }
}

function normalizeObservation(
  libraryHash,
  applicationID,
  {
    name,
    firstObservedTime: firstSeen,
    lastObservedTime: lastSeen,
    ...libraryUsageItem
  }
) {
  return {
    id: buildLibraryUsageItemID({ libraryHash, applicationID, name }),
    name,
    firstSeen,
    lastSeen,
    ...libraryUsageItem
  }
}

export const buildLibraryUsageItemID = ({
  libraryHash,
  applicationID,
  name
}: {
  readonly libraryHash: string
  readonly applicationID: string
  readonly name: string
}) => `${libraryHash}|${applicationID}|${btoa(name)}`

// This is used outside of the graphql layer, too, because we need it for non-json response requests
export const buildLibraryUsageQueryParams = ({
  offset,
  limit,
  filters,
  sortingOrder
}: {
  readonly offset?: number
  readonly limit?: number
  readonly filters: LibraryUsageFilterArg | undefined
  readonly sortingOrder: SkeletorTableState['sorting'] | undefined
}) => {
  const params = new ContrastQueryParameters()

  if (typeof offset !== 'undefined' && !isNaN(offset)) {
    params.append('offset', offset)
  }

  if (typeof limit !== 'undefined' && !isNaN(limit)) {
    params.append('limit', limit)
  }

  appendSortToParams(params, sortingOrder)

  if (filters && filters.filterText) {
    params.append('searchText', filters.filterText)
  }
  return params
}

export const libraryUsage: ContrastGraphQLFieldConfig<
  any,
  Context,
  LibraryUsagePagingArgs,
  LibraryUsagePage
> = {
  description: 'Get a page of library usage data',
  type: new GraphQLNonNull(libraryUsagePageType),
  args: libraryUsagePagingArgs,
  async resolve(_source, args, context): Promise<LibraryUsagePage> {
    const {
      organizationID,
      applicationID,
      libraryHash,
      offset,
      limit,
      filters,
      sortingOrder
    } = args

    const params = buildLibraryUsageQueryParams({
      offset,
      limit,
      filters,
      sortingOrder
    })

    const { observations, total } = await context.connector.get(
      `/organizations/${organizationID}/applications/${applicationID}/libraries/${libraryHash}/reports/library-usage?${params}`
    )

    const items = observations.map((libraryUsageItem) =>
      normalizeObservation(libraryHash, applicationID, libraryUsageItem)
    )

    return {
      items,
      total
    }
  }
}
